// Reading and writing zip files (an .xlsx is one) with the browser's own
// deflate (DecompressionStream / CompressionStream "deflate-raw", design 02
// 12.1). Only what xlsx needs: stored and deflated entries, no encryption,
// no zip64. Sizes are bounded so a small file cannot unpack into a huge one.

export const ZIP_LIMITS = {
  entries: 10_000,
  entryBytes: 256 * 1024 * 1024,
  totalBytes: 512 * 1024 * 1024,
};

export class ZipError extends Error {}

const SIG_LOCAL = 0x04034b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_END = 0x06054b50;

function u16(b: Uint8Array, at: number): number {
  return (b[at] ?? 0) | ((b[at + 1] ?? 0) << 8);
}

function u32(b: Uint8Array, at: number): number {
  return (u16(b, at) | (u16(b, at + 2) << 16)) >>> 0;
}

async function pipe(data: Uint8Array, stream: TransformStream<Uint8Array, Uint8Array>, max: number): Promise<Uint8Array> {
  const out: Uint8Array[] = [];
  let n = 0;
  const writer = stream.writable.getWriter();
  const writing = writer.write(data).then(() => writer.close());
  writing.catch(() => undefined);
  const reader = stream.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    n += value.length;
    if (n > max) {
      await reader.cancel();
      throw new ZipError("압축을 푼 크기가 너무 큽니다");
    }
    out.push(value);
  }
  await writing;
  const all = new Uint8Array(n);
  let at = 0;
  for (const part of out) {
    all.set(part, at);
    at += part.length;
  }
  return all;
}

export function inflateRaw(data: Uint8Array, max: number): Promise<Uint8Array> {
  return pipe(data, new DecompressionStream("deflate-raw") as unknown as TransformStream<Uint8Array, Uint8Array>, max);
}

export function deflateRaw(data: Uint8Array): Promise<Uint8Array> {
  return pipe(
    data,
    new CompressionStream("deflate-raw") as unknown as TransformStream<Uint8Array, Uint8Array>,
    Number.MAX_SAFE_INTEGER,
  );
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const utf8 = new TextDecoder("utf-8");
const cp437 = new TextDecoder("latin1");

/** The entries of a zip file by name (folders left out). */
export async function readZip(bytes: Uint8Array): Promise<Map<string, Uint8Array>> {
  // the end record is in the last 64 KiB + 22 bytes
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (u32(bytes, i) === SIG_END) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new ZipError("zip 파일이 아닙니다");
  const count = u16(bytes, end + 10);
  const dirAt = u32(bytes, end + 16);
  if (count > ZIP_LIMITS.entries) throw new ZipError("파일이 너무 많이 들어 있습니다");
  const files = new Map<string, Uint8Array>();
  let total = 0;
  let at = dirAt;
  for (let i = 0; i < count; i++) {
    if (at + 46 > bytes.length || u32(bytes, at) !== SIG_CENTRAL) throw new ZipError("zip 목록이 깨졌습니다");
    const flags = u16(bytes, at + 8);
    const method = u16(bytes, at + 10);
    const crc = u32(bytes, at + 16);
    const packed = u32(bytes, at + 20);
    const size = u32(bytes, at + 24);
    const nameLen = u16(bytes, at + 28);
    const extraLen = u16(bytes, at + 30);
    const commentLen = u16(bytes, at + 32);
    const local = u32(bytes, at + 42);
    const rawName = bytes.subarray(at + 46, at + 46 + nameLen);
    const name = (flags & 0x800 ? utf8 : cp437).decode(rawName);
    at += 46 + nameLen + extraLen + commentLen;
    if (name.endsWith("/")) continue;
    if (flags & 1) throw new ZipError("암호가 걸린 파일은 열 수 없습니다");
    if (size > ZIP_LIMITS.entryBytes) throw new ZipError("너무 큰 파일이 들어 있습니다");
    total += size;
    if (total > ZIP_LIMITS.totalBytes) throw new ZipError("압축을 푼 크기가 너무 큽니다");
    if (local + 30 > bytes.length || u32(bytes, local) !== SIG_LOCAL) throw new ZipError("zip 항목이 깨졌습니다");
    const dataAt = local + 30 + u16(bytes, local + 26) + u16(bytes, local + 28);
    if (dataAt + packed > bytes.length) throw new ZipError("zip 항목이 잘렸습니다");
    const raw = bytes.subarray(dataAt, dataAt + packed);
    let data: Uint8Array;
    if (method === 0) data = raw.slice();
    else if (method === 8) data = await inflateRaw(raw, size);
    else throw new ZipError(`지원하지 않는 압축 방식(${method})입니다`);
    if (data.length !== size || crc32(data) !== crc) throw new ZipError(`${name}: 내용이 맞지 않습니다`);
    files.set(name, data);
  }
  return files;
}

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/** A zip file of the entries, deflated. Times are left at 1980-01-01. */
export async function writeZip(entries: ZipEntry[]): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const e of entries) {
    const name = enc.encode(e.name);
    const crc = crc32(e.data);
    const packed = await deflateRaw(e.data);
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, SIG_LOCAL, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0x800, true);
    lv.setUint16(8, 8, true);
    lv.setUint16(10, 0, true);
    lv.setUint16(12, 0x21, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, packed.length, true);
    lv.setUint32(22, e.data.length, true);
    lv.setUint16(26, name.length, true);
    local.set(name, 30);
    const dir = new Uint8Array(46 + name.length);
    const dv = new DataView(dir.buffer);
    dv.setUint32(0, SIG_CENTRAL, true);
    dv.setUint16(4, 20, true);
    dv.setUint16(6, 20, true);
    dv.setUint16(8, 0x800, true);
    dv.setUint16(10, 8, true);
    dv.setUint16(14, 0x21, true);
    dv.setUint32(16, crc, true);
    dv.setUint32(20, packed.length, true);
    dv.setUint32(24, e.data.length, true);
    dv.setUint16(28, name.length, true);
    dv.setUint32(42, offset, true);
    dir.set(name, 46);
    parts.push(local, packed);
    central.push(dir);
    offset += local.length + packed.length;
  }
  const dirSize = central.reduce((n, d) => n + d.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, SIG_END, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, dirSize, true);
  ev.setUint32(16, offset, true);
  const all = new Uint8Array(offset + dirSize + 22);
  let at = 0;
  for (const p of [...parts, ...central, end]) {
    all.set(p, at);
    at += p.length;
  }
  return all;
}
